import { applyHospitalJourneyQuality } from '@/data/hospitalJourneyQuality';
import { translateJourneyValueWithSource } from './journeyTranslationFallback.js';

const LEGACY_JOURNEY_ROLE_KEYS = Object.freeze({
  '698db91853146ce9190809e8': 'midwife',
  'db64c7cc742f8f759b7c7e4f': 'sonographer',
  '6995ab52ef5e4f4f81bd9d41': 'obstetrician',
  '6992fceade00c9562dec1948': 'anaesthetist',
  '06089a3b339b85cf219be552': 'health_visitor',
});

// Eleven generic templates were derived from earlier, already-translated Journey
// narratives. Prefer a dedicated generic-* translation when one exists, then
// reuse the reviewed source translation before falling back to English. This
// avoids discarding good translation work while still allowing the generic
// wording to diverge safely over time.
const GENERIC_SOURCE_TRANSLATION_IDS = Object.freeze({
  'generic-day-surgery': 'kch-day-surgery',
  'generic-maternity': 'kch-maternity',
  'generic-outpatients': 'kch-outpatient-department',
  'generic-emergency-department': 'kch-emergency-department',
  'generic-imaging-diagnostics': 'kch-imaging-diagnostics',
  'generic-inpatient-ward': 'kch-inpatient-ward',
  'generic-critical-care-visitor': 'kch-critical-care-visitor',
  'generic-surgery-operation': 'kch-planned-inpatient-surgery',
  'generic-inpatient-admission': 'kch-planned-inpatient-medical',
  'generic-cancer-care': 'kch-cancer-care',
  'generic-paediatrics': 'kch-childrens-services',
});

function canonicalJourneyRoleReference(reference) {
  const value = String(reference || '').trim();
  return LEGACY_JOURNEY_ROLE_KEYS[value] || value;
}

function translateValue(t, key, fallback, options = {}) {
  if (!fallback) return fallback;
  const translated = t(`hospitalJourney:${key}`, {
    defaultValue: fallback,
    ...options,
  });

  const normalized = String(translated || '').trim();
  if (!normalized || normalized === key || normalized === `hospitalJourney:${key}`) {
    return fallback;
  }

  return translated;
}

function translateJourneyValue(t, journeyId, suffix, fallback) {
  return translateJourneyValueWithSource(journeyId, suffix, fallback, t, GENERIC_SOURCE_TRANSLATION_IDS);
}

function buildLocalizedRoleMap(roles = []) {
  const map = new Map();
  for (const role of roles) {
    if (!role) continue;
    const references = [
      role.id,
      role.role_key,
      ...(Array.isArray(role.legacy_alias_ids) ? role.legacy_alias_ids : []),
      ...(Array.isArray(role.legacy_alias_role_keys) ? role.legacy_alias_role_keys : []),
    ].filter(Boolean);

    for (const reference of references) {
      map.set(reference, role);
      map.set(canonicalJourneyRoleReference(reference), role);
    }
  }
  return map;
}

function localTextIsLocked(journey, section, id, field) {
  const locks = journey?.localTextOverrides || {};
  if (section === 'title') return Boolean(locks.title);
  return Boolean(locks?.[section]?.[id]?.[field]);
}

export function localizeHospitalJourney(journey, t, localizedRoles = []) {
  if (!journey) return null;

  const qualityJourney = applyHospitalJourneyQuality(journey);
  const roleMap = buildLocalizedRoleMap(localizedRoles);

  return {
    ...qualityJourney,
    title: localTextIsLocked(qualityJourney, 'title')
      ? qualityJourney.title
      : translateJourneyValue(t, qualityJourney.id, 'title', qualityJourney.title),
    eyebrow: translateValue(t, 'ui.eyebrow', qualityJourney.eyebrow || 'Hospital Journey'),
    introduction: translateJourneyValue(t, qualityJourney.id, 'introduction', qualityJourney.introduction),
    completion: qualityJourney.completion
      ? {
          ...qualityJourney.completion,
          label: translateValue(t, 'ui.completion.summaryLabel', qualityJourney.completion.label),
          title: translateValue(t, 'ui.completion.compactTitle', qualityJourney.completion.title),
          description: translateValue(t, 'ui.completion.compactDescription', qualityJourney.completion.description),
        }
      : qualityJourney.completion,
    exploreMore: qualityJourney.exploreMore
      ? {
          ...qualityJourney.exploreMore,
          title: translateJourneyValue(t, qualityJourney.id, 'exploreMore.title', qualityJourney.exploreMore.title),
          description: translateJourneyValue(t, qualityJourney.id, 'exploreMore.description', qualityJourney.exploreMore.description),
          linkLabel: translateJourneyValue(t, qualityJourney.id, 'exploreMore.linkLabel', qualityJourney.exploreMore.linkLabel),
        }
      : null,
    stages: (qualityJourney.stages || []).map((stage) => ({
      ...stage,
      title: localTextIsLocked(qualityJourney, 'stages', stage.id, 'title')
        ? stage.title
        : translateJourneyValue(t, qualityJourney.id, `stages.${stage.id}.title`, stage.title),
      description: localTextIsLocked(qualityJourney, 'stages', stage.id, 'description')
        ? stage.description
        : translateJourneyValue(t, qualityJourney.id, `stages.${stage.id}.description`, stage.description),
    })),
    // Journey configs hold role references rather than a second role dataset. Resolve
    // each reference to the current patient-facing role so links, progress ticks and
    // translated labels all use the same canonical role ID. Historic Mongo-style
    // Maternity IDs are normalised to canonical role keys before lookup so they can
    // never bypass the translated role library and fall back to an English title.
    roles: (qualityJourney.roles || []).map((roleReference) => {
      const requestedReference = canonicalJourneyRoleReference(roleReference.id || roleReference.role_key);
      const role = roleMap.get(requestedReference) || roleMap.get(roleReference.id);
      return {
        ...roleReference,
        id: role?.id || requestedReference || roleReference.id,
        role_key: role?.role_key || requestedReference || roleReference.role_key,
        title: role?.title || role?.plain_english_title || translateJourneyValue(t, qualityJourney.id,
          `roles.${requestedReference || roleReference.id}.title`, roleReference.title),
      };
    }),
    usefulLinks: (qualityJourney.usefulLinks || []).map((item) => ({
      ...item,
      title: localTextIsLocked(qualityJourney, 'usefulLinks', item.id, 'title')
        ? item.title
        : translateJourneyValue(t, qualityJourney.id, `usefulLinks.${item.id}.title`, item.title),
      description: translateJourneyValue(t, qualityJourney.id, `usefulLinks.${item.id}.description`, item.description),
    })),
    questions: (qualityJourney.questions || []).map((question, index) =>
      translateJourneyValue(t, qualityJourney.id, `questions.${index}`, question)
    ),
    disclaimer: translateJourneyValue(t, qualityJourney.id, 'disclaimer', qualityJourney.disclaimer),
  };
}

export function localizeHospitalJourneyTitle(journey, t) {
  if (!journey) return '';
  return localTextIsLocked(journey, 'title')
    ? journey.title
    : translateJourneyValue(t, journey.id, 'title', journey.title);
}
